import { root_path, subscribe_path, update_payment_path, user_dashboard_path } from '@routes';
import { SUPPORT_EMAIL } from '@utils/constants';

export type BillingErrorCode =
  | 'billing_card_declined'
  | 'billing_card_expired'
  | 'billing_card_invalid_cvc'
  | 'billing_card_invalid_number'
  | 'billing_card_processing_error'
  | 'billing_payment_method_invalid'
  | 'billing_payment_method_not_found'
  | 'billing_already_subscribed'
  | 'billing_offer_expired'
  | 'billing_offer_unavailable'
  | 'billing_subscription_paused'
  | 'billing_subscription_inactive'
  | 'billing_payment_failed'
  | 'billing_past_due'
  | 'billing_invoice_retry_exhausted'
  | 'billing_invoice_creation_failed'
  | 'billing_rate_limited'
  | 'billing_too_many_attempts'
  | 'billing_subscription_attempt_limit'
  | 'billing_group_recipients_empty'
  | 'billing_group_subscription_failed'
  | 'billing_tax_calculation_failed'
  | 'billing_customer_fetch_failed'
  | 'billing_card_expiring_soon'
  | 'billing_subscription_ending'
  | 'billing_charge_succeeded_update_failed'
  | 'billing_unknown_error';

export type CtaType =
  | 'update_payment'
  | 'try_again'
  | 'view_offers'
  | 'resume_subscription'
  | 'enable_subscription'
  | 'add_payment_method'
  | 'contact_support'
  | 'go_homepage';

export interface BillingErrorPayload {
  error_code: BillingErrorCode;
  message: string;
  cta_type: CtaType;
  cta_link: string | null;
  recoverable: boolean;
  stripe_code: string | null;
  stripe_message: string | null;
  flow: string;
}

export const BILLING_UNKNOWN_ERROR: BillingErrorPayload = {
  error_code: 'billing_unknown_error',
  message: `Something went wrong. Please try again later, or reach out to ${SUPPORT_EMAIL}.`,
  cta_type: 'contact_support',
  cta_link: `mailto:${SUPPORT_EMAIL}`,
  recoverable: false,
  stripe_code: null,
  stripe_message: null,
  flow: 'unknown',
};

interface CtaConfig {
  label: string;
  link: () => string | null;
}

const CTA_TYPE_REGISTRY: Record<CtaType, CtaConfig> = {
  try_again: { label: 'Try again', link: () => null },
  update_payment: { label: 'Update payment method', link: () => update_payment_path() },
  add_payment_method: { label: 'Add payment method', link: () => update_payment_path() },
  view_offers: { label: 'View offers', link: () => subscribe_path() },
  resume_subscription: { label: 'Resume your subscription', link: () => user_dashboard_path() },
  enable_subscription: { label: 'Enable your subscription', link: () => subscribe_path() },
  go_homepage: { label: 'Go to homepage', link: () => root_path() },
  contact_support: { label: 'Contact support', link: () => `mailto:${SUPPORT_EMAIL}` },
};

const reportedBillingDrift = new Set<string>();

function reportBillingDrift(reason: string, extra: Record<string, unknown>): void {
  if (typeof window === 'undefined' || !window.Rollbar?.warning) return;

  const key = `${reason}:${JSON.stringify(extra)}`;
  if (reportedBillingDrift.has(key)) return;
  reportedBillingDrift.add(key);

  window.Rollbar.warning(reason, extra);
}

export function getCtaConfig(ctaType?: CtaType | string | null): CtaConfig | null {
  if (!ctaType) return null;

  const config = CTA_TYPE_REGISTRY[ctaType as CtaType];
  if (!config) {
    reportBillingDrift('BillingError: unmapped cta_type', { cta_type: ctaType });
  }
  return config ?? null;
}

const BILLING_CODE_CTA: Record<BillingErrorCode, CtaType> = {
  billing_card_declined: 'update_payment',
  billing_card_expired: 'update_payment',
  billing_card_invalid_cvc: 'try_again',
  billing_card_invalid_number: 'try_again',
  billing_card_processing_error: 'try_again',
  billing_payment_method_invalid: 'update_payment',
  billing_payment_method_not_found: 'add_payment_method',
  billing_already_subscribed: 'go_homepage',
  billing_offer_expired: 'view_offers',
  billing_offer_unavailable: 'view_offers',
  billing_subscription_paused: 'resume_subscription',
  billing_subscription_inactive: 'enable_subscription',
  billing_payment_failed: 'try_again',
  billing_past_due: 'update_payment',
  billing_invoice_retry_exhausted: 'update_payment',
  billing_invoice_creation_failed: 'try_again',
  billing_rate_limited: 'try_again',
  billing_too_many_attempts: 'contact_support',
  billing_subscription_attempt_limit: 'try_again',
  billing_group_recipients_empty: 'contact_support',
  billing_group_subscription_failed: 'contact_support',
  billing_tax_calculation_failed: 'try_again',
  billing_customer_fetch_failed: 'try_again',
  billing_card_expiring_soon: 'update_payment',
  billing_subscription_ending: 'resume_subscription',
  billing_charge_succeeded_update_failed: 'contact_support',
  billing_unknown_error: 'contact_support',
};

// billing_payment_failed is the one code whose link diverges from its
// cta_type's default: the registry points it at the subscribe page even
// though try_again is otherwise linkless.
const BILLING_CODE_LINK_OVERRIDE: Partial<Record<BillingErrorCode, () => string>> = {
  billing_payment_failed: () => subscribe_path(),
};

interface ResolvedCta {
  cta_type: CtaType;
  cta_link: string | null;
}

export function getCtaForCode(code?: BillingErrorCode | string | null): ResolvedCta {
  const mapped = BILLING_CODE_CTA[code as BillingErrorCode];
  if (!mapped && code) {
    reportBillingDrift('BillingError: unmapped billing code', { error_code: code });
  }

  const ctaType = mapped ?? 'contact_support';
  const override = BILLING_CODE_LINK_OVERRIDE[code as BillingErrorCode];
  return {
    cta_type: ctaType,
    cta_link: override ? override() : CTA_TYPE_REGISTRY[ctaType].link(),
  };
}

export type BillingErrorLike = Partial<BillingErrorPayload> & Pick<BillingErrorPayload, 'message'>;

function classifiableBillingInput(error: unknown): Record<string, unknown> | null {
  if (typeof error === 'string' && error.length > 0) return { raw_error: error };

  if (error && typeof error === 'object') {
    const partial = error as Partial<BillingErrorPayload>;
    if (partial.error_code || partial.stripe_code) {
      return {
        error_code: partial.error_code ?? null,
        stripe_code: partial.stripe_code ?? null,
      };
    }
  }

  return null;
}

export function normalizeBillingError(
  error: unknown,
  fallbackMessage?: string
): BillingErrorPayload {
  const base = fallbackMessage
    ? { ...BILLING_UNKNOWN_ERROR, message: fallbackMessage }
    : BILLING_UNKNOWN_ERROR;
  if (error && typeof error === 'object' && 'message' in error) {
    const partial = error as Partial<BillingErrorPayload>;
    if (partial.cta_type) {
      return { ...base, ...partial };
    }
    const cta: ResolvedCta = partial.error_code
      ? getCtaForCode(partial.error_code)
      : { cta_type: 'try_again', cta_link: null };
    return { ...base, ...partial, ...cta };
  }

  const classified = classifiableBillingInput(error);
  if (classified) {
    reportBillingDrift('BillingError: unclassified billing error', classified);
  }
  return base;
}
